import React, {
  createContext,
  useState,
  useRef,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
} from "react";
import { useAtom } from 'jotai';
import { showRecordingPreviewAtom, hideRecordingPreviewAtom, isBlankModeAtom, currentSceneAtom } from "../atoms/sceneAtoms";
import { store } from "../store/canvasStoreInstance";
import { recordingQualityAtom } from "../store/canvasStore";
import { LayoutPreset } from "../presets/layoutPresets";
import { useMediaRecorder } from "../hooks/useMediaRecorder";
import { useSeparateStreamRecorder } from "../hooks/useSeparateStreamRecorder";
import { useStuckChunksHandler } from "../hooks/useStuckChunksHandler";
import { useInternetConnection } from "../hooks/useInternetConnection";
import { useUploadNotifications } from "../hooks/useUploadNotifications";
import logger from "../utils/logger";
import { useUserStore } from "../hooks/useUserStore";
import {
  shouldShowRetryButton,
  markLastChunk,
  db,
  areAllChunksDone,
} from "../idb/chunk-db";
import { TChunkStatus } from "../types/db";
import { resSize } from "../utils/resSize";
import { RECORDING_FLAGS } from "../constants/recordingFlags";

interface RecorderStreams {
  screen?: MediaStream;
  webcam?: MediaStream;
  canvas?: MediaStream;
  microphone?: MediaStream;
}

interface RecordedBlobs {
  screen?: Blob;
  webcam?: Blob;
  canvas?: Blob;
  microphone?: Blob;
}

type StreamType = "screen" | "webcam" | null;

export type CanvasInfo = {
  name: "background" | "webcam" | "screen";
  type?: "color" | "gradient" | "image";
  value?: any;
  width?: number;
  height?: number;
  ratio?: any;
  x?: number;
  y?: number;
};

export type GuideLine = {
  points: [number, number, number, number];
};

export type RecordingQuality = "720p" | "1080p" | "2k";

export interface RecorderContextProps {
  isRecording: boolean;
  recordingPending: boolean;
  isPaused: boolean;
  isUploading: boolean;
  recordedBlobs: RecordedBlobs;
  startRecording: (isReset?: boolean, overrideSceneId?: string) => void | Promise<void>;
  stopRecording: () => void;
  pauseRecording: () => void;
  resumeRecording: () => void;
  deleteRecording: () => void;
  stopSingleStreamRecording: (key: keyof RecorderStreams) => Promise<void>;
  toggleSeparateStream: (
    streamType: 'screen' | 'webcam',
    active: boolean,
    position?: { x: number; y: number; width: number; height: number },
    stream?: MediaStream
  ) => Promise<void>;
  useSeparateStreamsMode: boolean;
  screenStream: MediaStream | null;
  setScreenStream: React.Dispatch<React.SetStateAction<MediaStream | null>>;
  webcamStream: MediaStream | null;
  setWebcamStream: React.Dispatch<React.SetStateAction<MediaStream | null>>;
  microphoneStream: MediaStream | null;
  setMicrophoneStream: React.Dispatch<React.SetStateAction<MediaStream | null>>;
  rawMicrophoneStream: MediaStream | null;
  setRawMicrophoneStream: React.Dispatch<
    React.SetStateAction<MediaStream | null>
  >;
  systemAudioStream: MediaStream | null;
  setSystemAudioStream: React.Dispatch<
    React.SetStateAction<MediaStream | null>
  >;
  isSystemAudioEnabled: boolean;
  setIsSystemAudioEnabled: React.Dispatch<React.SetStateAction<boolean>>;
  selectedStream: StreamType;
  setSelectedStream: React.Dispatch<React.SetStateAction<StreamType>>;
  backgroundImage: HTMLImageElement | null;
  setBackgroundImage: (img: HTMLImageElement | null) => void;
  backgroundColor: string | null;
  setBackgroundColor: React.Dispatch<React.SetStateAction<string | null>>;
  setIntendedBackgroundImage: (imageUrl: string, imageKey?: string) => void;
  updateBackgroundKey: (imageKey: string) => void;
  customBackgroundImages: Map<string, string>; // Map of custom image IDs to blob URLs
  addCustomBackgroundImage: (id: string, blobUrl: string) => void;
  removeCustomBackgroundImage: (id: string) => void;
  canvasWidth: number;
  canvasHeight: number;
  setCanvasWidth: (w: number) => void;
  setCanvasHeight: (h: number) => void;
  recordingQuality: RecordingQuality;
  setRecordingQuality: React.Dispatch<React.SetStateAction<RecordingQuality>>;
  initialStreamLayout: LayoutPreset;
  setInitialStreamLayout: React.Dispatch<React.SetStateAction<LayoutPreset>>;
  canvasRatio: string;
  setCanvasRatio: React.Dispatch<React.SetStateAction<string>>;
  selectedPreset: string;
  setSelectedPreset: React.Dispatch<React.SetStateAction<string>>;
  isManualPreset: boolean;
  setIsManualPreset: React.Dispatch<React.SetStateAction<boolean>>;
  hasManualModifications: boolean;
  setHasManualModifications: React.Dispatch<React.SetStateAction<boolean>>;
  webcamDevices: MediaDeviceInfo[];
  setWebcamDevices: React.Dispatch<React.SetStateAction<MediaDeviceInfo[]>>;
  roundedWebcam: boolean;
  setRoundedWebcam: React.Dispatch<React.SetStateAction<boolean>>;
  mirroredWebcam: boolean;
  setMirroredWebcam: React.Dispatch<React.SetStateAction<boolean>>;
  avatarWebcam: boolean;
  setAvatarWebcam: React.Dispatch<React.SetStateAction<boolean>>;
  avatarImage: HTMLImageElement | null;
  avatarImageLoaded: boolean;
  preloadAvatarImage: (avatarUrl: string) => Promise<void>;
  micDevices: MediaDeviceInfo[];
  setMicDevices: React.Dispatch<React.SetStateAction<MediaDeviceInfo[]>>;
  enableCountdown: boolean;
  setEnableCountdown: React.Dispatch<React.SetStateAction<boolean>>;
  showCountdown: boolean;
  setShowCountdown: React.Dispatch<React.SetStateAction<boolean>>;
  canvasReady: boolean;
  setCanvasReady: React.Dispatch<React.SetStateAction<boolean>>;
  stageRef: React.RefObject<any> | null;
  setStageRef: (ref: React.RefObject<any>) => void;
  canvasInfos: CanvasInfo[][];
  setCanvasInfos: React.Dispatch<React.SetStateAction<CanvasInfo[][]>>;
  recordingStartTime: number | null;
  setRecordingStartTime: React.Dispatch<React.SetStateAction<number | null>>;
  layoutVersion: number;
  setLayoutVersion: React.Dispatch<React.SetStateAction<number>>;
  guideLines: GuideLine[];
  setGuideLines: React.Dispatch<React.SetStateAction<GuideLine[]>>;
  showGrid: boolean;
  setShowGrid: React.Dispatch<React.SetStateAction<boolean>>;
  // Full screen share detection
  isFullScreenShare: boolean;
  setIsFullScreenShare: React.Dispatch<React.SetStateAction<boolean>>;
  // Upload state tracking for beforeunload warning
  activeUploads: Set<string>;
  uploadInitializing: Set<string>;
  pendingChunks: Map<string, Array<{ chunkNo: number; size: number }>>;
  currentVideoGuid: string | null;
  // Retry state tracking
  uploadErrors: Map<string, string>;
  failedChunks: Map<string, Set<number>>;
  showRetryButton: boolean;
  setShowRetryButton: React.Dispatch<React.SetStateAction<boolean>>;
  retryUpload: (videoGuid: string, force?: boolean) => void;
  // Stuck chunks handling
  showStuckChunksOverlay: boolean;
  stuckChunks: Array<{
    videoGuid: string;
    uploadId?: string;
    key?: string;
    chunks: Array<{
      chunkNo: number;
      status: TChunkStatus;
      size: number;
      createdAt: number;
    }>;
  }>;
  totalStuckChunks: number;
  isProcessingStuckChunks: boolean;
  onRetryStuckChunks: () => void;
  onDiscardStuckChunks: () => void;
  isDeleting: boolean;
  // Download-only overlay state
  showDownloadOnlyOverlay: boolean;
  setShowDownloadOnlyOverlay: (show: boolean) => void;
  // Upload notifications
  completedUploads: Set<{
    videoId: string;
    videoGuid: string;
    timestamp: number;
  }>;
  handleUploadComplete: ({
    videoId,
    videoGuid,
  }: {
    videoId: string;
    videoGuid: string;
  }) => void;
  handleNotificationClose: (videoId: string) => void;
  clearAllNotifications: () => void;
  getLatestNotification: () => {
    videoId: string;
    videoGuid: string;
    timestamp: number;
  } | null;
  // Outside click state
  clickedOutside: boolean;
  setClickedOutside: React.Dispatch<React.SetStateAction<boolean>>;
  // Microphone mute state
  isMicrophoneMuted: boolean;
  setIsMicrophoneMuted: React.Dispatch<React.SetStateAction<boolean>>;
  setAvatarImage: React.Dispatch<React.SetStateAction<HTMLImageElement | null>>;
  setAvatarImageLoaded: React.Dispatch<React.SetStateAction<boolean>>;
  // Edge-to-edge mode (no padding)
  edgeToEdge: boolean;
  setEdgeToEdge: React.Dispatch<React.SetStateAction<boolean>>;
  // Scene-specific retake handler
  handleRetakeScene?: (sceneId: string) => Promise<void>;
  // Reset all recorder state (for navigation cleanup)
  resetRecorderState: () => void;
  // Interrupt blank recording for navigation (persist to IDB, don't upload)
  interruptBlankRecording: () => Promise<void>;
  // Abort recording (cancel in-progress recording and cleanup)
  abortRecording: () => Promise<void>;
  // MSTG (MediaStreamTrackGenerator) integration for precise frame timing
  mstgWriteFrame: ((canvas: HTMLCanvasElement, frameNumber: number, renderDurationMs: number) => Promise<unknown>) | null;
  isMSTGActive: () => boolean;
  hasMSTG: boolean;
  resetMSTGFrameCount: () => void;
  registerResetMSTGFrameCount: (resetFn: () => void) => void;
  // Canvas capture stream frame request (for captureStream(0) on-demand capture)
  requestCanvasFrame: () => boolean;
  // Separate stream recording methods for scene mode
  separateStreamPendingRecording: import('../hooks/useSeparateStreamRecorder').PendingRecording | null;
  uploadSeparateStreamRecording: () => Promise<void>;
  uploadFromRecoveredData: (recoveredData: any) => Promise<void>;
  clearSeparateStreamRecording: () => void;
}

export const RecorderContext = createContext<RecorderContextProps | undefined>(
  undefined
);

interface RecorderProviderProps {
  children: React.ReactNode;
}

export const RecorderProvider: React.FC<RecorderProviderProps> = ({
  children,
}) => {
  // Upload notifications hook
  const {
    completedUploads,
    handleUploadComplete,
    handleNotificationClose,
    clearAllNotifications,
    getLatestNotification,
    notifyUploadSuccess,
    notifyUploadFailure,
  } = useUploadNotifications();
  
  // Recording preview modal trigger
  const [, showRecordingPreview] = useAtom(showRecordingPreviewAtom);
  const [, hideRecordingPreview] = useAtom(hideRecordingPreviewAtom);
  
  // Main video/audio state
  const [isRecording, setIsRecording] = useState(false);
  const [recordingPending, setRecordingPending] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [recordedBlobs, setRecordedBlobs] = useState<RecordedBlobs>({});
  const [recordingStartTime, setRecordingStartTime] = useState<number | null>(
    null
  );

  const [screenStream, setScreenStream] = useState<MediaStream | null>(null);
  const [webcamStream, setWebcamStream] = useState<MediaStream | null>(null);
  const [microphoneStream, setMicrophoneStream] = useState<MediaStream | null>(
    null
  );
  const [rawMicrophoneStream, setRawMicrophoneStream] =
    useState<MediaStream | null>(null);
  const [systemAudioStream, setSystemAudioStream] =
    useState<MediaStream | null>(null);
  const [isSystemAudioEnabled, setIsSystemAudioEnabled] = useState(false);

  // Add debug logging for webcam stream changes
  // Debug logging disabled to reduce console bloat

  const [selectedStream, setSelectedStream] = useState<StreamType>(null);
  const [roundedWebcam, setRoundedWebcam] = useState<boolean>(false);
  const [mirroredWebcam, setMirroredWebcam] = useState<boolean>(false);
  const [avatarWebcam, setAvatarWebcam] = useState<boolean>(false);
  const [avatarImage, setAvatarImage] = useState<HTMLImageElement | null>(null);
  const [avatarImageLoaded, setAvatarImageLoaded] = useState<boolean>(false);

  const [backgroundImage, setBackgroundImageInternal] =
    useState<HTMLImageElement | null>(null);
  const [backgroundImageBitmap, setBackgroundImageBitmap] =
    useState<ImageBitmap | null>(null);
  const [backgroundColor, setBackgroundColorInternal] = useState<string | null>(null);
  
  // SINGLE SOURCE OF TRUTH: Store the intended background config
  // This captures what the background SHOULD be, even if image hasn't finished loading
  // Used by startRecording to always capture the correct background
  const [intendedBackground, setIntendedBackground] = useState<{
    type: 'image' | 'color' | 'gradient';
    value?: string;
    imageUrl?: string;
    imageKey?: string;
  } | null>(null);

  const setBackgroundImage = useCallback((img: HTMLImageElement | null) => {
    setBackgroundImageInternal(img);
    // Update intended background when image is set
    // Preserve existing imageKey if present (don't overwrite with blob URL only)
    if (img?.src) {
      setIntendedBackground(prev => {
        if (prev?.type === 'image' && prev.imageKey) {
          return { ...prev, imageUrl: img.src };
        }
        return { type: 'image', imageUrl: img.src };
      });
    }
  }, []);

  const setBackgroundColor = useCallback((color: React.SetStateAction<string | null>) => {
    setBackgroundColorInternal(color);
    // Update intended background when color is set
    if (typeof color === 'string' && color) {
      const isGradient = color.includes('gradient');
      setIntendedBackground({ 
        type: isGradient ? 'gradient' : 'color', 
        value: color 
      });
    }
  }, []);
  
  // Also update intended background when image URL is set but image is still loading
  const setIntendedBackgroundImage = useCallback((imageUrl: string, imageKey?: string) => {
    console.log('🖼️ [setIntendedBackgroundImage] Setting intended image background:', imageUrl, 'key:', imageKey);
    setIntendedBackground({ type: 'image', imageUrl, imageKey });
  }, []);
  const [customBackgroundImages, setCustomBackgroundImages] = useState<
    Map<string, string>
  >(new Map());
  const [stageRef, setStageRef] = useState<React.RefObject<any> | null>(null);

  const [canvasInfos, setCanvasInfos] = useState<CanvasInfo[][]>([]);
  const [canvasWidth, setCanvasWidth] = useState(1280);
  const [canvasHeight, setCanvasHeight] = useState(720);
  const [recordingQuality, setRecordingQuality] =
    useState<RecordingQuality>("1080p");

  // Sync Context recordingQuality to Jotai atom (single source of truth)
  // This keeps the atom in sync for consumers like layoutPresets.ts that read from the store
  useEffect(() => {
    store.set(recordingQualityAtom, recordingQuality);
  }, [recordingQuality]);

  // Get user subscription status for quality enforcement
  const { userData: userSubscriptionData } = useUserStore();
  const isPaidUser = userSubscriptionData?.isPaid === true;

  // Enforce 720p for free users - downgrade quality if not paid
  useEffect(() => {
    if (userSubscriptionData !== null && !isPaidUser && (recordingQuality === '1080p' || recordingQuality === '2k')) {
      setRecordingQuality('720p');
    }
  }, [userSubscriptionData, isPaidUser, recordingQuality]);

  const [initialStreamLayout, setInitialStreamLayoutInternal] = useState<LayoutPreset>(
    []
  );

  // Wrapper to deduplicate layout before setting
  const setInitialStreamLayout = useCallback((layoutOrUpdater: LayoutPreset | ((prev: LayoutPreset) => LayoutPreset)) => {
    setInitialStreamLayoutInternal((prev) => {
      // Get the new layout (either directly or from updater function)
      const newLayout = typeof layoutOrUpdater === 'function' 
        ? layoutOrUpdater(prev) 
        : layoutOrUpdater;
      
      // Deduplicate by stream type
      const seenStreams = new Set<string>();
      const deduplicated = newLayout.filter((item) => {
        if (seenStreams.has(item.stream)) {
          logger.warn(`🚨 RecorderContext: Duplicate stream detected and removed: ${item.stream}`);
          return false;
        }
        seenStreams.add(item.stream);
        return true;
      });
      
      return deduplicated;
    });
  }, []);

  // Debug logging disabled to reduce console bloat
  const [layoutVersion, setLayoutVersion] = useState(0);
  const [canvasRatio, setCanvasRatio] = useState("16:9");
  const [selectedPreset, setSelectedPreset] = useState("");
  const [isManualPreset, setIsManualPreset] = useState<boolean>(false);
  const [hasManualModifications, setHasManualModifications] =
    useState<boolean>(false);
  const [webcamDevices, setWebcamDevices] = useState<MediaDeviceInfo[]>([]);
  const [micDevices, setMicDevices] = useState<MediaDeviceInfo[]>([]);
  const [enableCountdown, setEnableCountdown] = useState(true);
  const [showCountdown, setShowCountdown] = useState(false);
  const [canvasReady, setCanvasReady] = useState(false);
  const [guideLines, setGuideLines] = useState<GuideLine[]>([]);
  const [showGrid, setShowGrid] = useState<boolean>(true);

  // Outside click state
  const [clickedOutside, setClickedOutside] = useState<boolean>(false);

  // Microphone mute state
  const [isMicrophoneMuted, setIsMicrophoneMuted] = useState<boolean>(true);

  // Edge-to-edge mode (no padding)
  const [edgeToEdge, setEdgeToEdge] = useState<boolean>(false);

  // Full screen share detection
  const [isFullScreenShare, setIsFullScreenShare] = useState<boolean>(false);

  // Reset all recorder state (for navigation cleanup)
  const resetRecorderState = useCallback(() => {
    // Reset all stream state
    setScreenStream(null);
    setWebcamStream(null);
    setMicrophoneStream(null);
    setRawMicrophoneStream(null);
    setSystemAudioStream(null);
    setIsSystemAudioEnabled(false);
    
    // Reset layout and canvas state
    setSelectedStream(null);
    setInitialStreamLayoutInternal([]);
    setCanvasInfos([]);
    setSelectedPreset("");
    setIsManualPreset(false);
    setHasManualModifications(false);
    setLayoutVersion(0);
    
    // Reset webcam/avatar state
    setRoundedWebcam(false);
    setMirroredWebcam(false);
    setAvatarWebcam(false);
    setAvatarImage(null);
    setAvatarImageLoaded(false);
    
    // Reset background state
    setBackgroundImage(null);
    setBackgroundImageBitmap(null);
    setBackgroundColor(null);
    setIntendedBackground(null);
    
    // Revoke custom background image URLs to free memory
    customBackgroundImages.forEach((blobUrl) => {
      URL.revokeObjectURL(blobUrl);
    });
    setCustomBackgroundImages(new Map());
    
    // Reset full screen share and edge-to-edge
    setIsFullScreenShare(false);
    setEdgeToEdge(false);
    
    // Reset microphone state
    setIsMicrophoneMuted(true);
    
    // Reset canvas ready state
    setCanvasReady(false);
    
    // Reset recording state
    setRecordedBlobs({});
    setRecordingStartTime(null);
    setShowCountdown(false);
  }, [customBackgroundImages]);

  // Retry state tracking
  const [uploadErrors, setUploadErrors] = useState<Map<string, string>>(
    new Map()
  );
  const [failedChunks, setFailedChunks] = useState<Map<string, Set<number>>>(
    new Map()
  );
  const [showRetryButton, setShowRetryButton] = useState<boolean>(false);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);

  // Helper function to check database status before showing retry button
  const checkAndSetRetryState = useCallback(
    async (videoGuid: string) => {
      try {
        // Don't show retry button if we're currently deleting/aborting
        if (isDeleting) {
          return;
        }
        const chunksDone = await areAllChunksDone(videoGuid);
        const shouldShow = await shouldShowRetryButton(videoGuid);
        if (shouldShow || chunksDone) {
          setIsProcessing(false);
          setShowRetryButton(true);
          setIsUploading(false);
        }
      } catch (error) {
        logger.error("Error checking chunk statuses for retry:", error);
        // Fallback to showing retry button if check fails
        setShowRetryButton(true);
        setIsUploading(false);
      }
    },
    [isDeleting]
  );

  useEffect(() => {
    // Debug logging disabled to reduce console bloat
    if (backgroundImage) {
      const img = new window.Image();
      img.crossOrigin = "anonymous";
      img.src = backgroundImage.src;
      img.onload = async () => {
        // Debug logging disabled to reduce console bloat
        const imageBitmap = await createImageBitmap(img);
        setBackgroundImageBitmap(imageBitmap);
      };
      img.onerror = () => {
        logger.error(
          "RecorderContext: Failed to load background image",
          backgroundImage.src
        );
      };
    } else {
      setBackgroundImageBitmap(null);
    }
  }, [backgroundImage]);

  // Preload avatar image when user data changes
  // Note: userSubscriptionData is already fetched above for quality enforcement

  const currentAvatarUrlRef = useRef<string | null>(null);

  useEffect(() => {
    const preloadAvatar = async () => {
      const fallbackUrl =
        "https://i.postimg.cc/SQvP9c0S/Chat-GPT-Image-Oct-5-2025-12-30-31-PM.png";
      const avatarUrl = userSubscriptionData?.imageThumbnailUrl || fallbackUrl;

      // Skip if we're already loading or have loaded this exact URL
      if (currentAvatarUrlRef.current === avatarUrl && avatarImageLoaded) {
        return;
      }

      currentAvatarUrlRef.current = avatarUrl;

      try {
        const img = new Image();
        img.crossOrigin = "anonymous";
        img.src = avatarUrl;

        await new Promise((resolve, reject) => {
          img.onload = resolve;
          img.onerror = reject;
        });

        // Only set the image if it's still the current URL (avoid race conditions)
        if (currentAvatarUrlRef.current === avatarUrl) {
          setAvatarImage(img);
          setAvatarImageLoaded(true);
        }
      } catch (error) {
        console.error("Failed to preload avatar image:", error);
        if (currentAvatarUrlRef.current === avatarUrl) {
          setAvatarImageLoaded(false);
        }
      }
    };

    if (userSubscriptionData) {
      preloadAvatar();
    }
  }, [userSubscriptionData?.imageThumbnailUrl]);

  // Extract system audio from screen stream (controlled by UI toggle)
  useEffect(() => {
    // logger.info(
    //   `System audio extraction: screenStream=${!!screenStream}, isSystemAudioEnabled=${isSystemAudioEnabled}`
    // );

    if (screenStream) {
      const audioTracks = screenStream.getAudioTracks();
      //logger.info(`Screen stream has ${audioTracks.length} audio tracks`);

      if (audioTracks.length > 0) {
        // Automatically enable system audio when screen sharing has audio
        if (!isSystemAudioEnabled) {
          setIsSystemAudioEnabled(true);
        }

        // Use the original screen stream directly for audio capture
        // This ensures we capture any audio that starts playing dynamically
        setSystemAudioStream(screenStream);
        // logger.info(
        //   "System audio stream set to screen stream for dynamic audio capture"
        // );
      } else {
        // No audio tracks available - automatically disable system audio toggle
        setSystemAudioStream(null);
        setIsSystemAudioEnabled(false);
        // logger.info(
        //   "Screen sharing active but no audio tracks available - disabling system audio toggle"
        // );
      }
    } else {
      setSystemAudioStream(null);
      setIsSystemAudioEnabled(false);
      //logger.info("No screen sharing active - disabling system audio toggle");
    }
  }, [screenStream]);

  // Native MediaRecorder API
  // MSTG frame counter reset callback - will be wired to useVideoTicker
  const resetMSTGFrameCountRef = useRef<() => void>(() => {});
  const resetMSTGFrameCount = useCallback(() => {
    resetMSTGFrameCountRef.current();
  }, []);
  const registerResetMSTGFrameCount = useCallback((resetFn: () => void) => {
    resetMSTGFrameCountRef.current = resetFn;
  }, []);

  const {
    isRecording: isFabricRecording,
    isPaused: isFabricPaused,
    startRecording: startFabricRecording,
    stopRecording: stopFabricRecording,
    pauseRecording: pauseFabricRecording,
    resumeRecording: resumeFabricRecording,
    deleteRecording: deleteFabricRecording,
    retryUpload: retryFabricUpload,
    currentVideoGuid,
    activeUploads,
    uploadInitializing,
    pendingChunks,
    showDownloadOnlyOverlay,
    setShowDownloadOnlyOverlay,
    getSceneIdForVideoGuid,
    mstgWriteFrame,
    hasMSTG,
    isMSTGActive,
    requestCanvasFrame,
  } = useMediaRecorder(
    stageRef,
    microphoneStream,
    systemAudioStream,
    recordingQuality,
    canvasWidth,
    canvasHeight,
    canvasRatio,
    setIsUploading,
    // Upload error callback
    (videoGuid: string, error: string) => {
      setUploadErrors((prev) => {
        const newMap = new Map(prev);
        newMap.set(videoGuid, error);
        return newMap;
      });
      // Check database status before showing retry button
      checkAndSetRetryState(videoGuid);
    },
    // Chunk error callback
    (videoGuid: string, chunkNo: number) => {
      // Don't add failed chunks if we're currently deleting/aborting
      if (isDeleting) {
        return;
      }

      setFailedChunks((prev) => {
        const newMap = new Map(prev);
        if (!newMap.has(videoGuid)) {
          newMap.set(videoGuid, new Set());
        }
        newMap.get(videoGuid)!.add(chunkNo);
        return newMap;
      });
      // Check database status before showing retry button
      checkAndSetRetryState(videoGuid);
    },
    // Upload complete callback
    handleUploadComplete,
    // MSTG recording start callback (resets frame counter)
    resetMSTGFrameCount,
  );

  // Separate stream recorder for blank mode (records screen + webcam as separate files)
  const [isBlankMode] = useAtom(isBlankModeAtom);
  const [currentScene] = useAtom(currentSceneAtom);
  
  // Callbacks for separate stream recorder to update global state
  const separateStreamRecorderCallbacks = useMemo(() => ({
    onUploadStart: () => {
      logger.info('[RecorderContext] Separate stream upload started');
      setIsUploading(true);
    },
    onUploadProgress: (progress: { screen: any; webcam: any }) => {
      logger.debug('[RecorderContext] Separate stream upload progress', progress);
    },
    onUploadComplete: (recordingId: string) => {
      logger.info('[RecorderContext] Separate stream upload complete', { recordingId });
      setIsUploading(false);
      // Show success notification using existing notification system
      notifyUploadSuccess(recordingId, isBlankMode);
    },
    onUploadError: (error: string) => {
      logger.error('[RecorderContext] Separate stream upload error', { error });
      setIsUploading(false);
      // Show error notification using existing notification system
      notifyUploadFailure('blank-recording', undefined, error);
    },
  }), [notifyUploadSuccess, notifyUploadFailure, isBlankMode]);
  
  const separateStreamRecorder = useSeparateStreamRecorder(separateStreamRecorderCallbacks);
  
  // Check if we should use separate streams mode (feature flag enabled - works for both blank and scene-based)
  const useSeparateStreamsMode = RECORDING_FLAGS.SEPARATE_STREAMS_MODE;

  // Abort recording (cancel in-progress recording and cleanup)
  const abortRecording = useCallback(async () => {
    logger.info('[RecorderContext] abortRecording called');
    console.log('[RecorderContext] abortRecording called');
    
    // Update UI state immediately
    setIsRecording(false);
    setRecordingStartTime(null);
    
    // Handle separate streams mode (blank mode)
    if (useSeparateStreamsMode) {
      logger.info('[RecorderContext] Aborting separate stream recording');
      await separateStreamRecorder.abortRecording();
      return;
    }
    
    // Handle regular recording mode - stop and cleanup without uploading
    logger.info('[RecorderContext] Aborting regular recording');
    setRecordedBlobs({});
  }, [useSeparateStreamsMode, separateStreamRecorder]);

  const interruptBlankRecording = useCallback(async () => {
    if (!useSeparateStreamsMode) return;
    if (!isBlankMode) return;
    if (!isRecordingRef.current && !separateStreamRecorder.state.isRecording) return;
    logger.info('[RecorderContext] interruptBlankRecording called (navigation guard)');
    isRecordingRef.current = false;
    setIsRecording(false);
    setRecordingStartTime(null);
    await separateStreamRecorder.interruptRecording();
  }, [useSeparateStreamsMode, isBlankMode, separateStreamRecorder]);

  // --- Start Recording
  const startRecording = useCallback(
    async (isReset: boolean = false, overrideSceneId?: string) => {
      logger.info('[RecorderContext] startRecording called', { isReset, overrideSceneId });
      
      // Clear all upload notifications when starting a new recording
      clearAllNotifications();

      const isPresentationImport = (currentScene as any)?.metadata?.sourceType === 'presentation-import';
      if ((isRecording && !isReset) || (!isPresentationImport && !screenStream && !webcamStream)) return;

      // Prevent starting new recording if there are active uploads (but only when not currently recording)
      if (
        !isRecording &&
        (isUploading || activeUploads.size > 0 || uploadInitializing.size > 0)
      ) {
        logger.warn("Cannot start recording while video is being uploaded");
        return;
      }

      // Hide any stale preview modal from previous recording before starting new one
      hideRecordingPreview();

      // On retake: clear any stale pending recording from the separate stream recorder
      // This ensures fresh state and prevents the preview modal from showing old data
      if (isReset && separateStreamRecorder.state.pendingRecording) {
        logger.info('[RecorderContext] Clearing stale pending recording before retake');
        await separateStreamRecorder.clearPendingRecording();
      }

      // Set recordingPending to protect against background/layout resets during startup
      setRecordingPending(true);
      
      logger.info('[RecorderContext] Starting separate stream recording', { isBlankMode, sceneId: overrideSceneId });
      
      // Get actual stream positions from initialStreamLayout
      const webcamLayoutItem = initialStreamLayout.find(item => item.stream === 'webcam');
      const screenLayoutItem = initialStreamLayout.find(item => item.stream === 'screen');
      
      const layout = {
        canvas: {
          width: canvasWidth,
          height: canvasHeight,
          aspectRatio: canvasRatio,
          quality: recordingQuality,
        },
        webcam: webcamStream ? {
          x: webcamLayoutItem?.x ?? 0,
          y: webcamLayoutItem?.y ?? 0,
          width: webcamLayoutItem?.width ?? canvasWidth,
          height: webcamLayoutItem?.height ?? canvasHeight,
          visible: true,
        } : undefined,
        screen: screenStream ? {
          x: screenLayoutItem?.x ?? 0,
          y: screenLayoutItem?.y ?? 0,
          width: screenLayoutItem?.width ?? canvasWidth,
          height: screenLayoutItem?.height ?? canvasHeight,
          visible: true,
        } : undefined,
        background: (() => {
          if (intendedBackground) {
            if (intendedBackground.type === 'image' && intendedBackground.imageUrl) {
              return { type: 'image' as const, imageUrl: intendedBackground.imageUrl, imageKey: intendedBackground.imageKey };
            }
            if (intendedBackground.type === 'gradient' && intendedBackground.value) {
              return { type: 'gradient' as const, value: intendedBackground.value };
            }
            if (intendedBackground.type === 'color' && intendedBackground.value) {
              return { type: 'color' as const, value: intendedBackground.value };
            }
          }
          
          if (backgroundImage?.src) {
            return { type: 'image' as const, imageUrl: backgroundImage.src };
          }
          if (backgroundColor) {
            const isGradient = backgroundColor.includes('gradient');
            return isGradient 
              ? { type: 'gradient' as const, value: backgroundColor }
              : { type: 'color' as const, value: backgroundColor };
          }
          
          return { type: 'color' as const, value: '#1a1a2e' };
        })(),
      };
      
      const avatarUrl = userSubscriptionData?.imageThumbnailUrl || avatarImage?.src || undefined;
      
      separateStreamRecorder.startRecording({
        screenStream,
        webcamStream,
        micStream: microphoneStream,
        systemAudioStream,
        layout,
        quality: recordingQuality,
        sceneId: isBlankMode ? undefined : overrideSceneId,
        roundedWebcam,
        mirroredWebcam,
        avatarWebcam,
        avatarUrl: avatarWebcam ? avatarUrl : undefined,
        edgeToEdge,
      });
      
      // Deselect all canvas objects and hide frames before recording starts
      const canvas = stageRef?.current?.getCanvas?.();
      if (canvas) {
        canvas.discardActiveObject();
        canvas.getObjects().forEach((obj: any) => {
          if (obj.name?.includes('-frame')) {
            obj.set({ visible: false, stroke: 'transparent', fill: 'transparent' });
          }
        });
        canvas.requestRenderAll();
      }
      
      setIsRecording(true);
      setRecordingPending(false);
      setRecordingStartTime(Date.now());
    },
    [
      isRecording,
      isUploading,
      activeUploads,
      uploadInitializing,
      clearAllNotifications,
      separateStreamRecorder,
      screenStream,
      webcamStream,
      microphoneStream,
      systemAudioStream,
      canvasWidth,
      canvasHeight,
      canvasRatio,
      recordingQuality,
      backgroundColor,
      avatarWebcam,
      avatarImage,
      userSubscriptionData,
      roundedWebcam,
      mirroredWebcam,
      currentScene,
      hideRecordingPreview,
    ]
  );

  // Track previous quality/ratio to detect changes
  const prevQualityRef = useRef(recordingQuality);
  const prevRatioRef = useRef(canvasRatio);

  useLayoutEffect(() => {
    const qualityChanged = prevQualityRef.current !== recordingQuality;
    const ratioChanged = prevRatioRef.current !== canvasRatio;
    prevQualityRef.current = recordingQuality;
    prevRatioRef.current = canvasRatio;

    const { width, height } = resSize(recordingQuality, canvasRatio);

    const applyDimensions = () => {
      setCanvasWidth(width);
      setCanvasHeight(height);
    };

    if (qualityChanged && !ratioChanged) {
      const rafId = requestAnimationFrame(() => {
        requestAnimationFrame(applyDimensions);
      });
      return () => cancelAnimationFrame(rafId);
    } else {
      applyDimensions();
    }
  }, [canvasRatio, recordingQuality]);

  const isRecordingRef = useRef(isRecording);

  useEffect(() => {
    isRecordingRef.current = isRecording;
  }, [isRecording]);

  // --- Stop Recording
  const stopRecording = useCallback(async () => {
    logger.info('[RecorderContext] stopRecording called', {
      isRecordingRef: isRecordingRef.current,
      useSeparateStreamsMode,
      separateStreamRecorderIsRecording: separateStreamRecorder.state.isRecording,
    });
    
    if (!isRecordingRef.current) {
      logger.warn('[RecorderContext] stopRecording called but isRecordingRef is false');
      return;
    }

    // Helper function to restore canvas object visibility after recording
    const restoreCanvasObjects = () => {
      const canvas = stageRef?.current?.getCanvas?.();
      if (canvas) {
        canvas.getObjects().forEach((obj: any) => {
          if (obj.name?.includes('-frame')) {
            obj.set({ visible: true });
          }
        });
        canvas.requestRenderAll();
      }
    };

    // All recordings use separate stream recorder
    // Update UI immediately for instant responsiveness
    setIsRecording(false);
    setRecordingStartTime(null);
    
    // Restore canvas object visibility after recording stops
    restoreCanvasObjects();
    
    // Check if we're in scene mode or blank mode
    const isSceneModeSeparateStreams = !isBlankMode;
    
    if (isSceneModeSeparateStreams) {
      logger.info('[RecorderContext] Stopping separate stream recording (scene mode - deferred upload)');
      
      // For scene mode: stop recording, get pending recording, show preview modal
      separateStreamRecorder.stopRecording().then((pendingRecording) => {
        if (pendingRecording) {
          logger.info('[RecorderContext] Separate stream recording stopped, showing preview', {
            sceneId: pendingRecording.sceneId,
            duration: pendingRecording.duration,
          });
          
          // Show the recording preview modal with blob URL for playback
          showRecordingPreview({
            mode: 'scene',
            sceneId: pendingRecording.sceneId || undefined,
            duration: pendingRecording.duration,
            micEnabled: pendingRecording.micEnabled,
          });
        }
      }).catch((error) => {
        logger.error('[RecorderContext] Separate stream stop failed', error);
      });
    } else {
      logger.info('[RecorderContext] Stopping separate stream recording (blank mode - immediate upload)');
      
      // For blank mode: stop and upload immediately in background
      separateStreamRecorder.stopRecording().catch((error) => {
        logger.error('[RecorderContext] Separate stream stop/upload failed', error);
      });
    }
  }, [showRecordingPreview, useSeparateStreamsMode, separateStreamRecorder, isBlankMode]);

  // --- Pause Recording
  const pauseRecording = useCallback(() => {
    if (!isRecordingRef.current) return;

    // All recordings use separate stream recorder
    separateStreamRecorder.pauseRecording();
  }, [separateStreamRecorder]);

  // --- Resume Recording
  const resumeRecording = useCallback(() => {
    if (!isRecordingRef.current) return;

    // All recordings use separate stream recorder
    separateStreamRecorder.resumeRecording();
  }, [separateStreamRecorder]);

  // --- Delete Recording
  const deleteRecording = useCallback(() => {
    // Set deleting flag to prevent retry button from showing
    setIsDeleting(true);

    setRecordingStartTime(null);
    setIsUploading(false); // Don't show saving overlay when deleting

    // Clear retry-related states
    setUploadErrors(new Map());
    setFailedChunks(new Map());
    setShowRetryButton(false);
    setIsRecording(false);
    // Call Fabric.js delete function
    deleteFabricRecording();
    
    // CRITICAL: Restore canvas object visibility after deleting recording
    const canvas = stageRef?.current?.getCanvas?.();
    if (canvas) {
      canvas.getObjects().forEach((obj: any) => {
        if (obj.name?.includes('-frame')) {
          obj.set({ visible: true });
        }
      });
      canvas.requestRenderAll();
    }

    // Reset deleting flag after a delay to allow cleanup to complete
    setTimeout(() => {
      setIsDeleting(false);
      setIsRecording(false);
    }, 2000);
  }, [deleteFabricRecording, stageRef]);

  // Device list updates
  useEffect(() => {
    const loadDevices = async () => {
      try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        setWebcamDevices(devices.filter((d) => d.kind === "videoinput"));
        setMicDevices(devices.filter((d) => d.kind === "audioinput"));
      } catch (err) {
        logger.error("Failed to enumerate devices:", err);
      }
    };

    loadDevices();
    navigator.mediaDevices.addEventListener("devicechange", loadDevices);
    return () => {
      navigator.mediaDevices.removeEventListener("devicechange", loadDevices);
    };
  }, []);

  // Stub for stopping single stream (can implement as needed)
  const stopSingleStreamRecording = useCallback(
    async (key: keyof RecorderStreams) => {
      //logger.warn("Not implemented: stopSingleStreamRecording for", key);
    },
    []
  );

  // Toggle stream on/off during recording (for blank mode)
  const toggleSeparateStream = useCallback(
    async (
      streamType: 'screen' | 'webcam',
      active: boolean,
      position?: { x: number; y: number; width: number; height: number },
      passedStream?: MediaStream
    ) => {
      if (!useSeparateStreamsMode) {
        logger.warn('[RecorderContext] toggleSeparateStream called but not in separate streams mode');
        return;
      }
      
      if (!separateStreamRecorder.state.isRecording) {
        logger.warn('[RecorderContext] toggleSeparateStream called but not recording');
        return;
      }
      
      // Use passed stream first (for mid-recording toggling), fallback to context stream
      const stream = active 
        ? (passedStream || (streamType === 'screen' ? screenStream : webcamStream))
        : undefined;
      
      // Get avatar URL - use avatarImage.src (includes fallback) to match canvas display
      const avatarUrl = userSubscriptionData?.imageThumbnailUrl || avatarImage?.src || undefined;
      
      logger.info('[RecorderContext] toggleSeparateStream', { streamType, active, position, hasStream: !!stream, roundedWebcam, avatarWebcam });
      
      await separateStreamRecorder.toggleStream(streamType, active, { 
        position, 
        stream, 
        roundedWebcam,
        avatarWebcam,
        avatarUrl: avatarWebcam ? avatarUrl : undefined,
      });
    },
    [useSeparateStreamsMode, separateStreamRecorder, screenStream, webcamStream, roundedWebcam, avatarWebcam, userSubscriptionData, avatarImage]
  );

  // Track previous layout for detecting stream visibility changes during recording
  const prevLayoutRef = useRef<Map<string, { x: number; y: number; width: number; height: number }>>(new Map());
  const layoutChangeQueueRef = useRef<Promise<void>>(Promise.resolve());
  const recordingInitializedRef = useRef(false);

  // Watch for layout changes during recording to trigger stream toggles
  useEffect(() => {
    if (!useSeparateStreamsMode) {
      // Reset tracking when not in separate streams mode
      prevLayoutRef.current = new Map();
      recordingInitializedRef.current = false;
      return;
    }

    if (!isRecording) {
      // When not recording, update ref with current layout for comparison when recording starts
      const currentLayout = new Map<string, { x: number; y: number; width: number; height: number }>();
      for (const item of initialStreamLayout) {
        currentLayout.set(item.stream, {
          x: item.x,
          y: item.y,
          width: item.width,
          height: item.height,
        });
      }
      prevLayoutRef.current = currentLayout;
      recordingInitializedRef.current = false;
      return;
    }

    // Skip the first layout update after recording starts (initial layout is handled in startRecording)
    if (!recordingInitializedRef.current) {
      recordingInitializedRef.current = true;
      const currentLayout = new Map<string, { x: number; y: number; width: number; height: number }>();
      for (const item of initialStreamLayout) {
        currentLayout.set(item.stream, {
          x: item.x,
          y: item.y,
          width: item.width,
          height: item.height,
        });
      }
      prevLayoutRef.current = currentLayout;
      logger.info('[RecorderContext] Layout watcher initialized for recording');
      return;
    }

    // Build current layout map
    const currentLayout = new Map<string, { x: number; y: number; width: number; height: number }>();
    for (const item of initialStreamLayout) {
      currentLayout.set(item.stream, {
        x: item.x,
        y: item.y,
        width: item.width,
        height: item.height,
      });
    }

    const prevLayout = prevLayoutRef.current;
    const currentStreams = new Set(currentLayout.keys());
    const prevStreams = new Set(prevLayout.keys());

    // Serialize toggle operations to avoid race conditions
    layoutChangeQueueRef.current = layoutChangeQueueRef.current.then(async () => {
      // Detect removed streams first (stop recording those segments)
      for (const stream of Array.from(prevStreams)) {
        if (!currentStreams.has(stream)) {
          logger.info('[RecorderContext] Layout change detected - stream removed:', { stream });
          await toggleSeparateStream(stream as 'screen' | 'webcam', false);
        }
      }

      // Detect added streams (start recording new segments)
      for (const stream of Array.from(currentStreams)) {
        if (!prevStreams.has(stream)) {
          const position = currentLayout.get(stream);
          if (position) {
            logger.info('[RecorderContext] Layout change detected - stream added:', { stream, position });
            await toggleSeparateStream(stream as 'screen' | 'webcam', true, position);
          }
        }
      }

      // Detect position changes for existing streams (create new segment with updated position)
      for (const stream of Array.from(currentStreams)) {
        if (prevStreams.has(stream)) {
          const prevPos = prevLayout.get(stream);
          const currPos = currentLayout.get(stream);
          if (prevPos && currPos && (
            prevPos.x !== currPos.x ||
            prevPos.y !== currPos.y ||
            prevPos.width !== currPos.width ||
            prevPos.height !== currPos.height
          )) {
            logger.info('[RecorderContext] Layout change detected - stream position changed:', {
              stream,
              from: prevPos,
              to: currPos,
            });
            // Close current segment and start new one with updated position
            await toggleSeparateStream(stream as 'screen' | 'webcam', false);
            await toggleSeparateStream(stream as 'screen' | 'webcam', true, currPos);
          }
        }
      }
    }).catch((err) => {
      logger.error('[RecorderContext] Error processing layout change:', err);
    });

    // Update ref for next comparison
    prevLayoutRef.current = currentLayout;
  }, [initialStreamLayout, isRecording, useSeparateStreamsMode, toggleSeparateStream]);

  // Custom background image management
  const addCustomBackgroundImage = useCallback(
    (id: string, blobUrl: string) => {
      setCustomBackgroundImages((prev) => {
        const newMap = new Map(prev);
        newMap.set(id, blobUrl);
        return newMap;
      });
    },
    []
  );

  const removeCustomBackgroundImage = useCallback((id: string) => {
    setCustomBackgroundImages((prev) => {
      const newMap = new Map(prev);
      const blobUrl = newMap.get(id);
      if (blobUrl) {
        // Revoke the blob URL to free memory
        URL.revokeObjectURL(blobUrl);
        newMap.delete(id);
      }
      return newMap;
    });
  }, []);

  // Retry upload function
  const retryUpload = useCallback(
    async (videoGuid: string, force: boolean = false) => {
      setIsUploading(true);

      logger.info(`Retrying upload for videoGuid: ${videoGuid}`);
      try {
        // Ensure the highest chunk number is marked as isLast during retry
        const allChunks = await db.chunks
          .where("videoGuid")
          .equals(videoGuid)
          .toArray();

        if (allChunks.length > 0) {
          const maxChunkNo = Math.max(
            ...allChunks.map((chunk) => chunk.chunkNo)
          );
          const maxChunk = allChunks.find(
            (chunk) => chunk.chunkNo === maxChunkNo
          );

          if (maxChunk && !maxChunk.isLast) {
            await markLastChunk(videoGuid, maxChunkNo);
            logger.info(
              `📤 Marked chunk #${maxChunkNo} as isLast during retry for ${videoGuid}`
            );
          }
        }

        // Use the fabric retry function
        await retryFabricUpload(videoGuid, force);

        // Clear error states
        setUploadErrors((prev) => {
          const newMap = new Map(prev);
          newMap.delete(videoGuid);
          return newMap;
        });
        setFailedChunks((prev) => {
          const newMap = new Map(prev);
          newMap.delete(videoGuid);
          return newMap;
        });
        setShowRetryButton(false);
      } catch (error) {
        logger.error(`Retry failed for videoGuid: ${videoGuid}`, error);
        // Keep error state for potential future retries
      }
    },
    [retryFabricUpload]
  );

  // Internet connection monitoring
  const { isOnline } = useInternetConnection();

  // Stuck chunks handler
  const {
    showOverlay: showStuckChunksOverlay,
    stuckChunks,
    totalChunks: totalStuckChunks,
    isProcessing: isProcessingStuckChunks,
    onRetry: onRetryStuckChunks,
    onDiscard: onDiscardStuckChunks,
    setIsProcessing,
  } = useStuckChunksHandler({
    retryUpload,
    isOnline,
    isUploading,
    setShowDownloadOnlyOverlay,
    setIsUploading,
    showDownloadOnlyOverlay,
  });

  return (
    <RecorderContext.Provider
      value={{
        isRecording,
        recordingPending,
        isPaused: separateStreamRecorder.state.isPaused,
        isUploading,
        recordedBlobs,
        startRecording,
        stopRecording,
        pauseRecording,
        resumeRecording,
        deleteRecording,
        stopSingleStreamRecording,
        toggleSeparateStream,
        useSeparateStreamsMode,
        screenStream,
        setScreenStream,
        webcamStream,
        setWebcamStream,
        microphoneStream,
        setMicrophoneStream,
        rawMicrophoneStream,
        setRawMicrophoneStream,
        systemAudioStream,
        setSystemAudioStream,
        isSystemAudioEnabled,
        setIsSystemAudioEnabled,
        selectedStream,
        setSelectedStream,
        backgroundImage,
        setBackgroundImage,
        setIntendedBackgroundImage,
        updateBackgroundKey: separateStreamRecorder.updateBackgroundKey,
        canvasWidth,
        canvasHeight,
        setCanvasWidth,
        setCanvasHeight,
        recordingQuality,
        setRecordingQuality,
        initialStreamLayout,
        setInitialStreamLayout,
        layoutVersion,
        setLayoutVersion,
        canvasRatio,
        setCanvasRatio,
        selectedPreset,
        setSelectedPreset,
        isManualPreset,
        setIsManualPreset,
        hasManualModifications,
        setHasManualModifications,
        webcamDevices,
        setWebcamDevices,
        micDevices,
        setMicDevices,
        backgroundColor,
        setBackgroundColor,
        customBackgroundImages,
        addCustomBackgroundImage,
        removeCustomBackgroundImage,
        enableCountdown,
        setEnableCountdown,
        showCountdown,
        setShowCountdown,
        canvasReady,
        setCanvasReady,
        stageRef,
        setStageRef,
        canvasInfos,
        setCanvasInfos,
        recordingStartTime,
        setRecordingStartTime,
        guideLines,
        setGuideLines,
        showGrid,
        setShowGrid,
        // Full screen share detection
        isFullScreenShare,
        setIsFullScreenShare,
        roundedWebcam,
        setRoundedWebcam,
        mirroredWebcam,
        setMirroredWebcam,
        avatarWebcam,
        setAvatarWebcam,
        avatarImage,
        avatarImageLoaded,
        preloadAvatarImage: async (avatarUrl: string) => {
          // Skip if we're already loading or have loaded this exact URL
          if (currentAvatarUrlRef.current === avatarUrl && avatarImageLoaded) {
            return;
          }

          currentAvatarUrlRef.current = avatarUrl;

          try {
            const img = new Image();
            img.crossOrigin = "anonymous";
            img.src = avatarUrl;

            await new Promise((resolve, reject) => {
              img.onload = resolve;
              img.onerror = reject;
            });

            // Only set the image if it's still the current URL (avoid race conditions)
            if (currentAvatarUrlRef.current === avatarUrl) {
              setAvatarImage(img);
              setAvatarImageLoaded(true);
            }
          } catch (error) {
            console.error("Failed to preload avatar image:", error);
            if (currentAvatarUrlRef.current === avatarUrl) {
              setAvatarImageLoaded(false);
            }
          }
        },
        // Upload state tracking for beforeunload warning
        activeUploads,
        uploadInitializing,
        pendingChunks,
        currentVideoGuid,
        // Retry state tracking
        uploadErrors,
        failedChunks,
        showRetryButton,
        setShowRetryButton,
        retryUpload,
        // Stuck chunks handling
        showStuckChunksOverlay,
        stuckChunks,
        totalStuckChunks,
        isProcessingStuckChunks,
        onRetryStuckChunks,
        onDiscardStuckChunks,
        isDeleting,
        // Download-only overlay state
        showDownloadOnlyOverlay,
        setShowDownloadOnlyOverlay,
        // Upload notifications
        completedUploads,
        handleUploadComplete,
        handleNotificationClose,
        clearAllNotifications,
        getLatestNotification,
        // Outside click state
        clickedOutside,
        setClickedOutside,
        // Microphone mute state
        isMicrophoneMuted,
        setIsMicrophoneMuted,
        setAvatarImage,
        setAvatarImageLoaded,
        // Edge-to-edge mode (no padding)
        edgeToEdge,
        setEdgeToEdge,
        // Reset all recorder state (for navigation cleanup)
        resetRecorderState,
        // Interrupt blank recording for navigation (persist to IDB, don't upload)
        interruptBlankRecording,
        // Abort recording (cancel in-progress recording and cleanup)
        abortRecording,
        // MSTG (MediaStreamTrackGenerator) integration for precise frame timing
        mstgWriteFrame,
        isMSTGActive,
        hasMSTG,
        resetMSTGFrameCount,
        registerResetMSTGFrameCount,
        // Canvas capture stream frame request (for captureStream(0))
        requestCanvasFrame,
        // Separate stream recording methods for scene mode
        separateStreamPendingRecording: separateStreamRecorder.state.pendingRecording,
        uploadSeparateStreamRecording: separateStreamRecorder.uploadRecording,
        uploadFromRecoveredData: separateStreamRecorder.uploadFromRecoveredData,
        clearSeparateStreamRecording: separateStreamRecorder.clearPendingRecording,
      }}
    >
      {children}
    </RecorderContext.Provider>
  );
};

// Custom hook for accessing RecorderContext
export const useRecorder = (): RecorderContextProps => {
  const context = React.useContext(RecorderContext);
  if (!context) {
    throw new Error('useRecorder must be used within a RecorderProvider');
  }
  return context;
};
